import { observer } from "mobx-react-lite";
import { Edit2, Copy, Trash2, Archive, RefreshCw, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTran } from "@/hooks";
import { customDisclosureStore } from "../../stores/custom-disclosure.store";

const CATEGORY_COLORS = {
  Environment: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  Social: "bg-purple-500/10 text-purple-600 border-purple-500/30",
  Governance: "bg-amber-500/10 text-amber-600 border-amber-500/30",
};

const DisclosureTableRow = observer(({ disclosure, onEdit }) => {
  const { t } = useTran();
  const { selectedIds, toggleSelection } = customDisclosureStore;
  const isSelected = selectedIds.includes(disclosure._id);

  return (
    <tr className="border-b border-border hover:bg-muted/50 transition-colors">
      <td className="p-3">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => toggleSelection(disclosure._id)}
          className="h-4 w-4"
        />
      </td>
      <td className="p-3">
        <div className="flex items-center gap-2">
          <code className="text-sm font-semibold text-foreground">
            {disclosure.code}
          </code>
          {disclosure.isTemplate && (
            <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
          )}
        </div>
      </td>
      <td className="p-3">
        <p className="text-sm text-foreground">{disclosure.title}</p>
        {disclosure.description && (
          <p className="text-xs text-muted-foreground truncate max-w-xs">
            {disclosure.description}
          </p>
        )}
      </td>
      <td className="p-3">
        <Badge className={`text-xs ${CATEGORY_COLORS[disclosure.category]}`}>
          {t(disclosure.category)}
        </Badge>
      </td>
      <td className="p-3">
        <p className="text-sm text-muted-foreground">{disclosure.inputType}</p>
      </td>
      <td className="p-3">
        <p className="text-sm text-foreground">
          {disclosure.usageCount || 0} {t("reports")}
        </p>
      </td>
      <td className="p-3">
        {disclosure.isActive ? (
          <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 text-xs">
            {t("Active")}
          </Badge>
        ) : (
          <Badge className="bg-muted text-muted-foreground text-xs">
            {t("Archived")}
          </Badge>
        )}
      </td>
      <td className="p-3">
        <div className="flex items-center justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              customDisclosureStore.handleToggleTemplate(
                disclosure._id,
                disclosure.isTemplate,
              )
            }
            className="h-8 w-8 p-0"
            title={
              disclosure.isTemplate
                ? t("Remove from templates")
                : t("Mark as template")
            }
          >
            <Star
              className={`h-4 w-4 ${
                disclosure.isTemplate ? "fill-amber-500 text-amber-500" : ""
              }`}
            />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onEdit(disclosure)}
            className="h-8 w-8 p-0"
            title={t("Edit")}
          >
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => customDisclosureStore.handleClone(disclosure._id)}
            className="h-8 w-8 p-0"
            title={t("Clone")}
          >
            <Copy className="h-4 w-4" />
          </Button>
          {disclosure.isActive ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                customDisclosureStore.handleArchive(disclosure._id)
              }
              className="h-8 w-8 p-0"
              title={t("Archive")}
            >
              <Archive className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                customDisclosureStore.handleRestore(disclosure._id)
              }
              className="h-8 w-8 p-0"
              title={t("Restore")}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => customDisclosureStore.handleDelete(disclosure._id)}
            className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
            title={t("Delete")}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
});

export default DisclosureTableRow;
